import { findIndex, uid } from "@dhx/ts-common/core";
import { IEventSystem } from "@dhx/ts-common/events";
import { DataCollection } from "./datacollection";
import { DataProxy } from "./dataproxy";
import { toDataDriver, isTreeCollection, copyWithoutInner } from "./helpers";
import { DataCallback, DataEvents, Id, IDataCollection, IDataItem, ITreeCollection, TreeFilterType, IFilterCallback, IFilterMode, ITreeFilterConfig, DataDriver, ISortMode } from "./types";
import { IAnyObj } from "@dhx/ts-common/types";

function addToOrder(store: any, obj: object, parent: Id, index: number) {
	if (index !== undefined && index !== -1 && store[parent] && store[parent][index]) {
		store[parent].splice(index, 0, obj);
	} else {
		if (!store[parent]) {
			store[parent] = [];
		}
		store[parent].push(obj);
	}
}


export class TreeCollection<T extends IDataItem = IDataItem> extends DataCollection<T> implements ITreeCollection<T> {

	protected _childs: { [id: string]: T[] };
	protected _root: Id;
	protected _filters: {
		[id: string]: {
			rule: IFilterMode | IFilterCallback,
			config: ITreeFilterConfig
		}
	};
	private _initChilds: { [id: string]: T[] };

	constructor(config?: any, events?: IEventSystem<DataEvents>) {
		super(config, events);
		const root = this._root = "_ROOT_" + uid();
		this._childs = { [root]: [] };
		this._initChilds = null;
	}
	add(obj: IDataItem, index?: number, parent?: Id): Id;
	add(obj: IDataItem[], index?: number, parent?: Id): Id[];
	add(obj: IDataItem | IDataItem[], index: number = -1, parent: Id = this._root): Id | Id[] {
		if (typeof obj !== "object") {
			obj = {
				value: obj
			};
		}
		if (Array.isArray(obj)) {
			return obj.map((element, key) => {
				if (key > 0 && index !== -1) {
					index = index + 1;
				}
				element.parent = element.parent ? element.parent.toString() : parent;
				const id = super.add(element, index);
				if (Array.isArray(element.items)) {
					for (const item of element.items) {
						this.add(item, -1, element.id);
					}
				}
				return id;
			});
		} else {
			obj.parent = obj.parent ? obj.parent.toString() : parent;
			const id = super.add(obj, index);

			if (Array.isArray(obj.items)) {
				for (const item of obj.items) {
					this.add(item, -1, obj.id);
				}
			}
			return id;
		}
	}
	getRoot(): Id {
		return this._root;
	}
	getParent(id: Id, asObj: boolean = false): Id {
		if (!this._pull[id]) {
			return null;
		}
		const parent = this._pull[id].parent;
		return asObj ? this._pull[parent] : parent;
	}
	getItems(id: Id): T[] {
		if (this._childs && this._childs[id]) {
			return this._childs[id];
		}
		return [];
	}
	getLength(id: Id = this._root): number {
		if (!this._childs[id]) {
			return null;
		}
		return this._childs[id].length;
	}
	removeAll(id?: Id): void {
		if (id) {
			const childs = [... this._childs[id]];
			for (const child of childs) {
				this.remove(child.id);
			}
		} else {
			super.removeAll();
			const root = this._root;
			this._initChilds = null;
			this._childs = { [root]: [] };
		}
	}
	getIndex(id: Id): number {
		const parent = this.getParent(id);
		if (!parent || !this._childs[parent]) {
			return -1;
		}
		return findIndex(this._childs[parent], item => item.id === id);
	}
	sort(by?: ISortMode): void {
		if (!by) {
			this._childs = {};
			// [dirty]
			this._parse_data(Object.keys(this._pull).map(key => this._pull[key]));
			if (this._filters) {
				for (const key in this._filters) {
					const filter = this._filters[key];
					this.filter(filter.rule, filter.config);
				}
			}
		} else {
			for (const key in this._childs) {
				this._sort.sort(this._childs[key], by);
			}
			if (this._initChilds && Object.keys(this._initChilds).length) {
				for (const key in this._initChilds) {
					this._sort.sort(this._initChilds[key], by);
				}
			}
		}

		this.events.fire(DataEvents.change);
	}
	map(cb: DataCallback<T>, parent: Id = this._root, direct: boolean = true): any[] {
		let result: any[] = [];
		if (!this.haveItems(parent)) {
			return result;
		}
		for (let i = 0; i < this._childs[parent].length; i++) {
			result.push(cb.call(this, this._childs[parent][i], i));
			if (direct) {
				const childResult = this.map(cb, this._childs[parent][i].id, direct);
				result = result.concat(childResult);
			}
		}
		return result;
	}
	filter(rule?: IFilterMode | IFilterCallback, config: ITreeFilterConfig = {}): void {
		if (!rule) {
			this.restoreOrder();
			return;
		}

		if (!this._initChilds) {
			this._initChilds = this._childs;
		}
		config.type = config.type || TreeFilterType.leafs;

		// [todo] we can store multiple filter rules, like in datacollection
		this._filters = {};
		this._filters._ = {
			rule,
			config
		};

		const newChilds = {};
		this._recursiveFilter(rule, config, this._root, 0, newChilds);
		const parents = [];
		for (const i in newChilds) {
			const check = newChilds[i].map((element) => {
				return !this.haveItems(element.id) && this.getParent(element.id) !== this.getRoot();
			}).find(item => item ? item : null);
			if (newChilds[i].length > 0 && newChilds[i] !== newChilds[this.getRoot()] && check) {
				const item = newChilds[this.getRoot()].find(function(element) {
					if (element.id === i) {
						return element;
					}
				});
				if (item) {
					parents.push(item);
				}
			}
		}
		newChilds[this.getRoot()] = parents;
		this._childs = newChilds;

		this.events.fire(DataEvents.change);
	}
	restoreOrder(): void {
		if (this._initChilds) {
			this._childs = this._initChilds;
			this._initChilds = null;
		}

		this.events.fire(DataEvents.change);
	}
	copy(id: Id, index: number, target: IDataCollection | ITreeCollection, targetId: Id): Id;
	copy(id: Id[], index: number, target: IDataCollection | ITreeCollection, targetId: Id): Id[];
	copy(id: Id | Id[], index: number, target: IDataCollection | ITreeCollection = this, targetId: Id = this._root): Id | Id[] {
		if (id instanceof Array) {
			return id.map((elementId, key) => {
				if (!this.exists(elementId)) {
					return null;
				}
				const currentChilds = this._childs[elementId];
				const elementIndex = index === -1 ? -1 : index + key;

				if (target === this && !this.canCopy(elementId, targetId)) {
					return null;
				}
				const itemCopy = copyWithoutInner(this.getItem(elementId), { items: true });
				if (target.exists(elementId)) {
					itemCopy.id = uid();
				}
				if (!isTreeCollection(target)) {
					target.add(itemCopy, elementIndex);
					return;
				}
				if (this.exists(elementId)) {
					itemCopy.parent = targetId;
					if (target !== this && targetId === this._root) {
						itemCopy.parent = target.getRoot();
					}
					target.add(itemCopy, elementIndex);
					elementId = itemCopy.id;
				}
				if (currentChilds) {
					for (const child of currentChilds) {
						const childId = child.id;
						const childIndex = this.getIndex(childId);
						if (typeof elementId === "string") {
							this.copy(childId, childIndex, target, elementId);
						}
					}
				}
				return elementId;
			});
		} else {
			if (!this.exists(id)) {
				return null;
			}
			const currentChilds = this._childs[id];
			if (target === this && !this.canCopy(id, targetId)) {
				return null;
			}
			const itemCopy = copyWithoutInner(this.getItem(id), { items: true });
			if (target.exists(id)) {
				itemCopy.id = uid();
			}
			if (!isTreeCollection(target)) {
				target.add(itemCopy, index);
				return;
			}
			if (this.exists(id)) {
				itemCopy.parent = targetId;
				if (target !== this && targetId === this._root) {
					itemCopy.parent = target.getRoot();
				}
				target.add(itemCopy, index);
				id = itemCopy.id;
			}
			if (currentChilds) {
				for (const child of currentChilds) {
					const childId = child.id;
					const childIndex = this.getIndex(childId);
					if (typeof id === "string") {
						this.copy(childId, childIndex, target, id);
					}
				}
			}
			return id;
		}
	}
	move(id: Id, index: number, target: ITreeCollection | IDataCollection, targetId: Id): Id;
	move(id: Id[], index: number, target: ITreeCollection | IDataCollection, targetId: Id): Id[];
	move(id: Id | Id[], index: number, target: ITreeCollection | IDataCollection = this, targetId: Id = this._root): Id | Id[] {
		if (id instanceof Array) {
			return id.map((elementId, key) => {
				if (!this.exists(elementId)) {
					return null;
				}
				let elementIndex = index === -1 ? -1 : index + key;

				if (target !== this) {
					if (!isTreeCollection(target)) {
						target.add(copyWithoutInner(this.getItem(elementId)), elementIndex);
						this.remove(elementId);
						return;
					}
					const returnId = this.copy(elementId, elementIndex, target, targetId);
					this.remove(elementId);
					return returnId;
				}

				if (!this.canCopy(elementId, targetId)) {
					return null;
				}
				const parent = this.getParent(elementId);
				const parentIndex = this.getIndex(elementId);

				const spliced = this._childs[parent].splice(parentIndex, 1)[0];
				(spliced as IDataItem).parent = targetId;

				if (!this._childs[parent].length) {
					delete this._childs[parent];
				}
				if (!this.haveItems(targetId)) {
					this._childs[targetId] = [];
				}
				if (elementIndex === -1) {
					elementIndex = this._childs[targetId].push(spliced);
				} else {
					this._childs[targetId].splice(elementIndex, 0, spliced);
				}

				this.events.fire(DataEvents.change);
				return elementId;
			});
		} else {
			if (!this.exists(id)) {
				return null;
			}

			if (target !== this) {
				if (!isTreeCollection(target)) { // move to datacollection
					target.add(copyWithoutInner(this.getItem(id)), index);
					this.remove(id);
					return;
				}
				const returnId = this.copy(id, index, target, targetId);
				this.remove(id);
				return returnId;
			}
			// move inside
			if (!this.canCopy(id, targetId)) {
				return null;
			}
			const parent = this.getParent(id);
			const parentIndex = this.getIndex(id);

			// get item from parent array and move to target array
			const spliced = this._childs[parent].splice(parentIndex, 1)[0];
			(spliced as IDataItem).parent = targetId; // need for next moving, ... not best solution, may be full method for get item

			if (!this._childs[parent].length) {
				delete this._childs[parent];
			}
			if (!this.haveItems(targetId)) {
				this._childs[targetId] = [];
			}
			if (index === -1) {
				index = this._childs[targetId].push(spliced);
			} else {
				this._childs[targetId].splice(index, 0, spliced);
			}

			this.events.fire(DataEvents.change);
			return id;
		}
	}
	eachChild(id: Id, cb: DataCallback<T>, direct: boolean = true, checkItem: (item: IDataItem) => boolean = () => true): void {
		if (!this.haveItems(id)) {
			return;
		}
		for (let i = 0; i < this._childs[id].length; i++) {
			cb.call(this, this._childs[id][i], i);
			if (direct && checkItem(this._childs[id][i])) {
				this.eachChild(this._childs[id][i].id, cb, direct, checkItem);
			}
		}
	}
	getNearId(id: Id): Id {
		return id; // for selection
	}
	loadItems(id: Id, driver: any = "json"): void {
		const url = this.config.autoload + "?id=" + id;
		const proxy = new DataProxy(url);
		proxy.load().then((data) => {
			driver = toDataDriver(driver);
			data = driver.toJsonArray(data);
			this._parse_data(data, id);

			this.events.fire(DataEvents.change);
		});
	}
	refreshItems(id: Id, driver: any = "json"): void {
		this.removeAll(id);
		this.loadItems(id, driver);
	}
	eachParent(id: Id, cb: DataCallback<T>, self: boolean = false): void {
		const item = this.getItem(id);
		if (!item) {
			return;
		}
		if (self) {
			cb.call(this, item);
		}
		if (item.parent === this._root) {
			return;
		}
		const parent = this.getItem(item.parent);
		cb.call(this, parent);
		this.eachParent(item.parent, cb);
	}
	haveItems(id: Id): boolean {
		return id in this._childs;
	}
	canCopy(id: Id, target: Id): boolean {
		if (id === target) {
			return false;
		}
		let canCopy = true;
		this.eachParent(target, item => item.id === id ? canCopy = false : null); // locate return string
		return canCopy;
	}
	serialize(driver: DataDriver = DataDriver.json, checkItem?: (item: any) => any) {
		const data = this._serialize(this._root, checkItem);
		const dataDriver = toDataDriver(driver);
		if (dataDriver) {
			return dataDriver.serialize(data);
		}
	}
	getId(index: number, parent: string = this._root): string {
		if (!this._childs[parent] || !this._childs[parent][index]) {
			return;
		}
		return this._childs[parent][index].id;
	}
	protected _removeAll(id?: Id) {
		if (id) {
			const childs = [... this._childs[id]];
			for (const child of childs) {
				this.remove(child.id);
			}
		} else {
			super._removeAll();
			const root = this._root;
			this._initChilds = null;
			this._childs = { [root]: [] };
		}
	}
	protected _removeCore(id) {
		if (this._pull[id]) {
			const parent = this.getParent(id);
			this._childs[parent] = this._childs[parent].filter(item => item.id !== id);
			if (parent !== this._root && !this._childs[parent].length) {
				delete this._childs[parent];
			}
			if (this._initChilds && this._initChilds[parent]) {
				this._initChilds[parent] = this._initChilds[parent].filter(item => item.id !== id);
				if (parent !== this._root && !this._initChilds[parent].length) {
					delete this._initChilds[parent];
				}
			}
			this._fastDeleteChilds(this._childs, id);
			if (this._initChilds) {
				this._fastDeleteChilds(this._initChilds, id);
			}
		}
	}
	protected _addToOrder(_order, obj: any, index: number): void {
		const childs = this._childs;
		const initChilds = this._initChilds;
		const parent = obj.parent;
		this._pull[obj.id] = obj;

		addToOrder(childs, obj, parent, index);
		if (initChilds) {
			addToOrder(initChilds, obj, parent, index);
		}
	}
	protected _parse_data(data: any, parent = this._root) {
		for (let obj of data) {
			if (this.config.init) {
				obj = this.config.init(obj);
			}
			if (typeof obj !== "object") {
				obj = {
					value: obj
				};
			}
			obj.id = obj.id ? obj.id.toString() : uid();
			obj.parent = obj.parent ? obj.parent.toString() : parent;
			this._pull[obj.id] = obj;

			if (!this._childs[obj.parent]) {
				this._childs[obj.parent] = [];
			}
			this._childs[obj.parent].push(obj);
			if (obj.items && obj.items instanceof Object) {
				this._parse_data(obj.items, obj.id);
			}
		}
	}
	private _fastDeleteChilds(target, id: Id): void {
		if (this._pull[id]) {
			delete this._pull[id];
		}
		if (!target[id]) {
			return;
		}
		for (let i = 0; i < target[id].length; i++) {
			this._fastDeleteChilds(target, target[id][i].id);
		}
		delete target[id];
	}
	private _recursiveFilter(rule: IFilterMode | IFilterCallback, config: ITreeFilterConfig, current: Id, level: number, newChilds: IAnyObj): void {
		const childs = this._childs[current];
		if (!childs) {
			return;
		}
		const condition = (item: T): boolean => {
			switch (config.type) {
				case TreeFilterType.all: {
					return true;
				}
				case TreeFilterType.level: {
					return level === config.level;
				}
				case TreeFilterType.leafs: {
					return !this.haveItems(item.id);
				}
			}
		};
		if (typeof rule === "function") {
			const customRule = (item: T) => !condition(item) || rule(item);
			const filtered = childs.filter(customRule);
			if (filtered.length) {
				newChilds[current] = filtered;
			}
		} else if ((rule as IFilterMode).by && (rule as IFilterMode).match) {
			const customRule = (item: T) => !condition(item) || item[rule.by].toString().toLowerCase().indexOf(rule.match.toString().toLowerCase()) !== -1;
			newChilds[current] = childs.filter(customRule);
		}
		for (const child of childs) {
			this._recursiveFilter(rule, config, child.id, level + 1, newChilds);
		}
	}
	private _serialize(parent = this._root, fn?) {
		return this.map(item => {
			let itemCopy: any = {};
			for (const key in item) {
				if (key === "parent" || key === "items") {
					continue;
				}
				itemCopy[key] = item[key];
			}
			if (fn) {
				itemCopy = fn(itemCopy);
			}
			if (this.haveItems(item.id)) {
				itemCopy.items = this._serialize(item.id, fn);
			}
			return itemCopy;
		}, parent, false);
	}
}